import {
  Box,
  Checkbox,
  ClickAwayListener,
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
  Paper,
  Popper,
  TextFieldProps,
  useTheme,
} from "@mui/material";
import { Controller, useFieldArray } from "react-hook-form";
import Chip from "../Chip/Chip";
import { useRef, useState } from "react";

type User = {
  email: string;
  name: string;
};

type ChipListInputProps = {
  name: string;
  control: any;
  required?: boolean;
  options?: User[]; 
};

const ChipListInputDropDown = ({
  name,
  control,
  label,
  required = false,
  placeholder,
  variant = "outlined",
  fullWidth = false,
  sx,
  autoComplete = "off",
  options = [], 
}: ChipListInputProps & TextFieldProps) => {
  const theme = useTheme();
  const { append, remove } = useFieldArray({
    control,
    name: name,
  });
  
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef(null);
  
  const filteredOptions = options.filter(user => 
    user.email.toLowerCase().includes(inputValue.toLowerCase()) ||
    user.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  const isUserSelected = (email: string, selectedUsers: User[]) => {
    return selectedUsers.some(user => user.email === email);
  };

  const findUserIndexByEmail = (email: string, selectedUsers: User[]) => {
    return selectedUsers.findIndex(user => user.email === email);
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: { value: required, message: `${label} is required *` },
      }}
      render={({ field: { value = [] }, fieldState: { error } }) => (
        <Box>
          <FormControl 
            fullWidth={fullWidth} 
            variant={variant as any} 
            error={!!error}
            sx={{
              ...sx,
              marginTop: theme.spacing(3),
              marginBottom: theme.spacing(2),
            }}
          >
            <InputLabel>{label}</InputLabel>
            <OutlinedInput
              ref={anchorRef}
              value={inputValue}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setInputValue(event.target.value);
                if (!isOpen) setIsOpen(true);
              }}
              onClick={() => setIsOpen(!isOpen)}
              autoComplete={autoComplete}
              placeholder={placeholder}
              label={label}
            />
            {error && <FormHelperText>{error.root?.message}</FormHelperText>}
          </FormControl>
          
          <Popper
            open={isOpen}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            style={{ zIndex: 1300 }}
          >
            <ClickAwayListener onClickAway={() => setIsOpen(false)}>
              <Paper elevation={3} sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List dense>
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((user) => {
                      const selected = isUserSelected(user.email, value);
                      return (
                        <ListItem
                          key={user.email}
                          dense
                          button
                          onClick={() => {
                            if (selected) {
                              const index = findUserIndexByEmail(user.email, value);
                              if (index !== -1) {
                                remove(index);
                              }
                            } else {
                              append(user);
                            }
                          }}
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={selected}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={user.email} 
                            secondary={user.name}
                          />
                        </ListItem>
                      );
                    })
                  ) : (
                    <ListItem>
                      <ListItemText primary="No users found" />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </ClickAwayListener>
          </Popper>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {value.map((user: User) => (
              <Chip
                key={user.email}
                label={user.name}
                onDelete={() => {
                  const index = findUserIndexByEmail(user.email, value);
                  if (index !== -1) {
                    remove(index);
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    />
  );
};

export default ChipListInputDropDown;